import { useNavigate } from 'react-router-dom';
import { useCoreLayout, useAuthLayout } from '@contexts/layout';
import { useAuth } from '@contexts/auth/AuthContext';

/**
 * Hook for smooth navigation with layout animations.
 * Use this when you want to navigate with exit animations on routes that show/hide layout.
 */
export const useLayoutNavigation = () => {
  const navigate = useNavigate();
  useAuth();

  // Core layout features - always available
  const { setBackgroundColor, showGradient, hideGradient, isGradientVisible } = useCoreLayout();

  // Auth layout features - only when authenticated
  const authLayout = useAuthLayout();

  /**
   * Navigate with exit animation (for routes that will show layout)
   * This ensures navbar and header are shown when navigating to full layout routes
   */
  const navigateWithAnimation = (
    to: string,
    options?: { state?: Record<string, unknown>; replace?: boolean; showGradient?: boolean },
  ) => {
    // Only change gradient state if explicitly requested AND it's different from current state
    if (options?.showGradient !== undefined) {
      if (options.showGradient && !isGradientVisible) {
        showGradient();
      } else if (!options.showGradient && isGradientVisible) {
        hideGradient();
      }
    }

    if (authLayout) {
      authLayout.triggerExit(() => {
        navigate(to, options);
        // Ensure navbar and header are shown after navigation to full layout routes
        setTimeout(() => {
          authLayout.showNavbarAndHeader();
        }, 50); // Small delay to ensure navigation has completed
      });
    } else {
      // No auth layout available - navigate immediately
      navigate(to, options);
    }
  };

  /**
   * Navigate to a hidden layout route (e.g., purchase screens)
   * This will play the exit animation and then navigate to a route where layout is hidden
   */
  const navigateToHiddenRoute = (
    to: string,
    options?: { state?: Record<string, unknown>; replace?: boolean; showGradient?: boolean },
  ) => {
    // Only change gradient state if explicitly requested AND it's different from current state
    if (options?.showGradient !== undefined) {
      if (options.showGradient && !isGradientVisible) {
        showGradient();
      } else if (!options.showGradient && isGradientVisible) {
        hideGradient();
      }
    }

    if (authLayout) {
      authLayout.triggerExit(() => {
        navigate(to, options);
      });
    } else {
      navigate(to, options);
    }
  };

  /**
   * Navigate with navbar/header exit animation (for routes that keep logo but hide navbar/header)
   * This will hide navbar and header with animation, then navigate
   */
  const navigateToLogoOnlyRoute = (
    to: string,
    options?: {
      state?: Record<string, unknown>;
      replace?: boolean;
      fadeToColor?: string;
      showGradient?: boolean;
    },
    callback?: () => void,
  ) => {
    // Only change gradient state if explicitly requested AND it's different from current state
    if (options?.showGradient !== undefined) {
      if (options.showGradient && !isGradientVisible) {
        showGradient();
      } else if (!options.showGradient && isGradientVisible) {
        hideGradient();
      }
      // If requested state matches current state, do nothing
    }

    if (authLayout) {
      authLayout.hideNavbarAndHeader(() => {
        navigate(to, options);
        callback?.();
      });
    } else {
      navigate(to, options);
      callback?.();
    }

    // Set background color if provided
    if (options?.fadeToColor) {
      setBackgroundColor(options.fadeToColor);
    }
  };

  /**
   * Navigate from a logo-only route back to a full layout route
   * This will fade out the current content, then navigate to a route where navbar/header will slide back in
   */
  const navigateBackToFullLayout = (
    to: string,
    options?: {
      state?: Record<string, unknown>;
      replace?: boolean;
      fadeToColor?: string;
      showGradient?: boolean;
    },
  ) => {
    // Only change gradient state if explicitly requested AND it's different from current state
    if (options?.showGradient !== undefined) {
      if (options.showGradient && !isGradientVisible) {
        showGradient();
      } else if (!options.showGradient && isGradientVisible) {
        hideGradient();
      }
    }

    if (authLayout) {
      authLayout.triggerExit(() => {
        navigate(to, options);
      });
    } else {
      navigate(to, options);
    }

    if (options?.fadeToColor) {
      setBackgroundColor(options.fadeToColor);
    }
  };

  /**
   * Navigate from a logo-only route back to a full layout route with content fade-out
   * This will fade out the current page content first, then navigate to prevent content jumping
   */
  const navigateFromLogoOnlyWithFadeOut = (
    to: string,
    fadeOutCallback: () => void,
    options?: {
      state?: Record<string, unknown>;
      replace?: boolean;
      fadeToColor?: string;
      showGradient?: boolean;
    },
  ) => {
    // Only change gradient state if explicitly requested AND it's different from current state
    if (options?.showGradient !== undefined) {
      if (options.showGradient && !isGradientVisible) {
        showGradient();
      } else if (!options.showGradient && isGradientVisible) {
        hideGradient();
      }
    }

    // Set background color if provided
    if (options?.fadeToColor) {
      setBackgroundColor(options.fadeToColor);
    }

    // Start the fade-out animation on the current page
    fadeOutCallback();

    // Wait for fade-out to complete, then navigate
    setTimeout(() => {
      navigate(to, { state: options?.state, replace: options?.replace });
    }, 300); // Match the fade-out duration
  };

  /**
   * Navigate immediately without animation (fallback to regular navigate)
   */
  const navigateImmediately = (
    to: string,
    options?: { state?: Record<string, unknown>; replace?: boolean; showGradient?: boolean },
  ) => {
    // Only change gradient state if explicitly requested AND it's different from current state
    if (options?.showGradient !== undefined) {
      if (options.showGradient && !isGradientVisible) {
        showGradient();
      } else if (!options.showGradient && isGradientVisible) {
        hideGradient();
      }
    }

    navigate(to, options);
  };

  return {
    navigateWithAnimation,
    navigateToHiddenRoute,
    navigateToLogoOnlyRoute,
    navigateImmediately,
    navigateBackToFullLayout,
    navigateFromLogoOnlyWithFadeOut,
    // Expose the available functions for advanced use cases
    setBackgroundColor,
    hideGradient,
    showGradient,
    // Auth layout functions (may be null if not authenticated)
    triggerExit: authLayout?.triggerExit || (() => {}),
    hideNavbarAndHeader: authLayout?.hideNavbarAndHeader || (() => {}),
    showNavbarAndHeader: authLayout?.showNavbarAndHeader || (() => {}),
  };
};

export default useLayoutNavigation;
